import { useEffect, useRef, useState } from 'react';
import type { AudioClip, AutomationLane, ClipGainPoint, EffectInstance, InstrumentState, Project } from '@/domain/types';
import { AUDIO_ACCEPT, pickFiles } from '@/storage/fileIO';
import { RIDE_FLOOR_DB, formatRideDb, rideGainFromDb, sortedGainPoints } from '@/domain/clipGain';
import { clipLengthTicks, effectivePlaybackRate, signatureMapOf } from '@/domain/selectors';
import {
  MIN_WARP_GAP_BEATS,
  MIN_WARP_GAP_SEC,
  beatToSourceSec,
  warpCurve,
  warpTotalBeats,
} from '@/domain/warpMap';
import { formatBarBeat } from '@/timing/tempoMap';
import { gainToDb, dbToGain } from '@/util/assert';
import type { AutomationTarget } from '@/domain/types';
import {
  MAX_PITCH_SEMITONES,
  MAX_PLAYBACK_RATE,
  MIN_PITCH_SEMITONES,
  MIN_PLAYBACK_RATE,
} from '@/domain/project';
import { command } from '@/commands/registry';
import * as ClipCommands from '@/commands/clipCommands';
import * as EffectCommands from '@/commands/effectCommands';
import * as AutomationCommands from '@/commands/automationCommands';
import * as MidiCommands from '@/commands/midiCommands';
import * as ClipGainCommands from '@/commands/clipGainCommands';
import { INSTRUMENT_DESCRIPTORS, instrumentDescriptor } from '@/engine/instruments/descriptors';
import { automationValueAt, describeTarget, resolveParamDescriptor } from '@/engine/automation';
import { denormaliseParam, effectDescriptor, formatParamValue, normaliseParam } from '@/engine/effects/descriptors';
import { quantiseSemitones } from '@/engine/pitchShift';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';
import { SamplerZones } from './SamplerZones';
import { compSources, trackTakeName } from '@/domain/takes';
import { takeLaneCount } from '@/ui/timeline/view';

/**
 * The inspector edits real state. Every control here dispatches a command, so
 * everything it changes is undoable and is reflected in the audio immediately.
 */
export function Inspector(): JSX.Element {
  const project = useDaw((s) => s.project);
  const selection = useDaw((s) => s.selection);
  const dispatch = useDaw((s) => s.dispatch);

  const clip = selection.clipIds.length === 1 ? project.clips[selection.clipIds[0]] : null;
  const track = selection.trackId ? project.tracks[selection.trackId] : null;
  const lane = selection.laneId ? project.automation[selection.laneId] : null;
  const sigMap = signatureMapOf(project);

  if (!clip && !track && !lane) {
    return <div className="empty-note">Select a clip, a track or an automation lane to inspect it.</div>;
  }

  return (
    <div className="inspector">
      {clip && clip.kind === 'audio' && (
        <ClipSection
          clip={clip}
          lengthLabel={formatBarBeat(sigMap, clipLengthTicks(project, clip))}
          rate={effectivePlaybackRate(project, clip)}
        />
      )}

      {selection.clipIds.length > 1 && (
        <div className="inspector__group">
          <div className="inspector__title">Selection</div>
          <div className="inspector__row">
            <span className="inspector__label">Clips</span>
            <span className="inspector__readout" style={{ gridColumn: '2 / 4' }}>
              {selection.clipIds.length} selected
            </span>
          </div>
        </div>
      )}

      {lane && (
        <div className="inspector__group">
          <div className="inspector__title">Automation — {describeTarget(project, lane.target)}</div>
          <div className="inspector__row">
            <span className="inspector__label">Points</span>
            <span className="inspector__readout" style={{ gridColumn: '2 / 4' }}>
              {lane.points.length}
              {selection.pointIds.length > 0 ? ` · ${selection.pointIds.length} selected` : ''}
            </span>
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Value here</span>
            <span className="inspector__readout" style={{ gridColumn: '2 / 4' }}>
              {formatLaneValue(project, lane, project.transport.playheadTicks)}
            </span>
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Curve</span>
            <div style={{ display: 'flex', gap: 4, gridColumn: '2 / 4' }}>
              {(['linear', 'hold', 'smooth'] as const).map((curve) => {
                const selectedPoints = lane.points.filter((p) => selection.pointIds.includes(p.id));
                const active = selectedPoints.length > 0 && selectedPoints.every((p) => p.curve === curve);
                return (
                  <button
                    key={curve}
                    className={`btn ${active ? 'btn--active' : ''}`}
                    style={{ height: 20, flex: 1 }}
                    disabled={selection.pointIds.length === 0}
                    title={CURVE_HELP[curve]}
                    onClick={() =>
                      dispatch(
                        command(AutomationCommands.setAutomationCurve, {
                          laneId: lane.id,
                          pointIds: selection.pointIds,
                          curve,
                        }),
                      )
                    }
                  >
                    {curve}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Shape</span>
            <div style={{ display: 'flex', gap: 4, gridColumn: '2 / 4' }}>
              {([
                ['Wider', 1.4],
                ['Narrower', 0.7],
                ['Flatten', 0],
              ] as const).map(([label, factor]) => (
                <button
                  key={label}
                  className="btn"
                  style={{ height: 20, flex: 1 }}
                  disabled={selection.pointIds.length < 2}
                  aria-label={`${label} the selected automation`}
                  title="Scale the selected points about the middle of their own range, so the gesture changes size without moving."
                  onClick={() =>
                    dispatch(
                      command(AutomationCommands.scaleAutomationPoints, {
                        laneId: lane.id,
                        pointIds: selection.pointIds,
                        factor,
                        pivot: null,
                      }),
                    )
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Thin</span>
            <div style={{ display: 'flex', gap: 4, gridColumn: '2 / 4' }}>
              {([
                ['Light', 0.005],
                ['Medium', 0.02],
                ['Heavy', 0.06],
              ] as const).map(([label, tolerance]) => (
                <button
                  key={label}
                  className="btn"
                  style={{ height: 20, flex: 1 }}
                  aria-label={`Thin the lane, ${label.toLowerCase()}`}
                  title={
                    selection.pointIds.length > 1
                      ? 'Remove points inside the selection that the line through their neighbours already covers.'
                      : 'Remove points anywhere in the lane that the line through their neighbours already covers.'
                  }
                  onClick={() =>
                    dispatch({
                      type: AutomationCommands.thinAutomationPoints.type,
                      params: {
                        laneId: lane.id,
                        pointIds: selection.pointIds.length > 1 ? selection.pointIds : null,
                        tolerance,
                      },
                    })
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Lane</span>
            <div style={{ display: 'flex', gap: 4, gridColumn: '2 / 4' }}>
              <button
                className="btn"
                style={{ height: 20, flex: 1 }}
                onClick={() => dispatch(command(AutomationCommands.clearAutomationLane, { laneId: lane.id }))}
              >
                Clear
              </button>
              <button
                className="btn btn--danger"
                style={{ height: 20, flex: 1 }}
                onClick={() => dispatch(command(AutomationCommands.removeAutomationLane, { laneId: lane.id }))}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {track && (
        <div className="inspector__group">
          <div className="inspector__title">Track — {track.name}</div>
          <div className="inspector__row">
            <span className="inspector__label">Kind</span>
            <span className="inspector__readout" style={{ gridColumn: '2 / 4' }}>
              {track.kind}
            </span>
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Clips</span>
            <span className="inspector__readout" style={{ gridColumn: '2 / 4' }}>
              {track.clipIds.length}
            </span>
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Colour</span>
            <input
              type="color"
              value={track.color}
              style={{ gridColumn: '2 / 4', width: 44, height: 20, background: 'none', border: 'none' }}
              aria-label="Track colour"
              onChange={(e) =>
                dispatch({ type: 'track.setColor', params: { trackId: track.id, color: e.target.value } })
              }
            />
          </div>
        </div>
      )}

      {track?.instrument && (
        <div className="inspector__group">
          <div className="inspector__title">Instrument</div>
          <div className="inspector__row">
            <span className="inspector__label">Type</span>
            <select
              className="select"
              style={{ height: 20, gridColumn: '2 / 4' }}
              value={track.instrument.type}
              aria-label="Instrument"
              onChange={(e) => {
                const instrumentType = e.target.value;
                if (instrumentType === 'wam') {
                  // A plugin is fetched from wherever its author put it, so
                  // the one thing to ask for is where.
                  const url = window.prompt('Web Audio Module URL — the address of the plugin instrument module (https://…/index.js)');
                  if (url) useDaw.getState().setWebAudioModuleInstrument(track.id, url);
                  return;
                }
                dispatch({ type: MidiCommands.setInstrument.type, params: { trackId: track.id, instrumentType } });
              }}
            >
              {Object.values(INSTRUMENT_DESCRIPTORS).map((d) => (
                <option key={d.type} value={d.type}>
                  {d.type === 'wam' ? `${d.label}…` : d.label}
                </option>
              ))}
            </select>
          </div>
          {track.instrument.type === 'wam' && <WamInstrumentRow trackId={track.id} instrument={track.instrument} />}
          {instrumentDescriptor(track.instrument).params.map((param) => {
            const value = track.instrument!.params[param.id] ?? param.default;
            return (
              <div className="inspector__row" key={param.id}>
                <span className="inspector__label">
                  <WritingDot
                    target={{ kind: 'instrument', trackId: track.id, effectId: null, paramId: param.id }}
                  />
                  {param.label}
                </span>
                {param.choices ? (
                  <select
                    className="select"
                    style={{ height: 20 }}
                    value={String(Math.round(value))}
                    aria-label={param.label}
                    onChange={(e) =>
                      dispatch(
                        command(MidiCommands.setInstrumentParam, {
                          trackId: track.id,
                          paramId: param.id,
                          value: Number(e.target.value),
                        }),
                      )
                    }
                  >
                    {param.choices.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="slider"
                    type="range"
                    min={param.min}
                    max={param.max}
                    step={param.step ?? (param.max - param.min) / 400}
                    value={value}
                    aria-label={param.label}
                    onPointerDown={() =>
                      useDaw.getState().touchParameter({
                        kind: 'instrument',
                        trackId: track.id,
                        effectId: null,
                        paramId: param.id,
                      })
                    }
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      dispatch(
                        command(MidiCommands.setInstrumentParam, {
                          trackId: track.id,
                          paramId: param.id,
                          value: next,
                        }),
                      );
                      useDaw.getState().writeParameter(
                        { kind: 'instrument', trackId: track.id, effectId: null, paramId: param.id },
                        normaliseParam(param, next),
                      );
                    }}
                    onPointerUp={() => {
                      useDaw.getState().releaseParameter({
                        kind: 'instrument',
                        trackId: track.id,
                        effectId: null,
                        paramId: param.id,
                      });
                      getServices().commands.breakCoalescing();
                    }}
                  />
                )}
                <span className="inspector__readout">{formatParamValue(param, value)}</span>
              </div>
            );
          })}
        </div>
      )}

      {track?.instrument?.type === 'sampler' && <SamplerZones track={track} />}

      {clip?.kind === 'midi' && (
        <div className="inspector__group">
          <div className="inspector__title">MIDI clip</div>
          <div className="inspector__row">
            <span className="inspector__label">Notes</span>
            <span className="inspector__readout" style={{ gridColumn: '2 / 4' }}>
              {clip.notes.length}
              {selection.noteIds.length > 0 ? ` · ${selection.noteIds.length} selected` : ''}
            </span>
          </div>
          <div className="inspector__row">
            <span className="inspector__label">Velocity</span>
            <input
              className="slider"
              type="range"
              min={1}
              max={127}
              step={1}
              disabled={selection.noteIds.length === 0}
              value={
                clip.notes.find((n) => n.id === selection.noteIds[0])?.velocity ?? 100
              }
              aria-label="Velocity of the selected notes"
              onChange={(e) =>
                dispatch(
                  command(MidiCommands.setNoteVelocity, {
                    clipId: clip.id,
                    noteIds: selection.noteIds,
                    velocity: Number(e.target.value),
                  }),
                )
              }
              onPointerUp={() => getServices().commands.breakCoalescing()}
            />
            <span className="inspector__readout">
              {selection.noteIds.length === 0
                ? '—'
                : (clip.notes.find((n) => n.id === selection.noteIds[0])?.velocity ?? 100)}
            </span>
          </div>
        </div>
      )}

      {track?.effects.map((effect) => {
        const descriptor = effectDescriptor(effect);
        return (
          <div className="inspector__group" key={effect.id}>
            <div className="inspector__title">
              {descriptor.label}
              {!effect.enabled && ' (bypassed)'}
            </div>
            {effect.type === 'reverb' && <ReverbRoomRow trackId={track.id} effect={effect} />}
            {effect.type === 'wam' && <WamRow trackId={track.id} effect={effect} />}
            {descriptor.params.map((param) => {
              const value = effect.params[param.id] ?? param.default;
              // A recorded room replaces the synthesised tail, so the tail's
              // own controls have nothing to do while one is loaded.
              const shapesTail = param.id === 'size' || param.id === 'damping';
              const roomLoaded = effect.type === 'reverb' && Boolean(effect.impulseAssetId);
              if (shapesTail && roomLoaded) return null;
              return (
                <div className="inspector__row" key={param.id}>
                  <span className="inspector__label">
                    <WritingDot
                      target={{
                        kind: 'effect',
                        trackId: track.id,
                        effectId: effect.id,
                        paramId: param.id,
                      }}
                    />
                    {param.label}
                  </span>
                  {param.choices ? (
                    <select
                      className="select"
                      style={{ height: 20 }}
                      value={String(Math.round(value))}
                      aria-label={`${descriptor.label} ${param.label}`}
                      onChange={(e) =>
                        dispatch(
                          command(EffectCommands.setEffectParam, {
                            trackId: track.id,
                            effectId: effect.id,
                            paramId: param.id,
                            value: Number(e.target.value),
                          }),
                        )
                      }
                    >
                      {param.choices.map((choice) => (
                        <option key={choice.value} value={choice.value}>
                          {choice.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="slider"
                      type="range"
                      min={param.min}
                      max={param.max}
                      step={(param.max - param.min) / 400}
                      value={value}
                      aria-label={`${descriptor.label} ${param.label}`}
                      onPointerDown={() =>
                        useDaw.getState().touchParameter({
                          kind: 'effect',
                          trackId: track.id,
                          effectId: effect.id,
                          paramId: param.id,
                        })
                      }
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        dispatch(
                          command(EffectCommands.setEffectParam, {
                            trackId: track.id,
                            effectId: effect.id,
                            paramId: param.id,
                            value: next,
                          }),
                        );
                        // A lane holds a normalised position, not the parameter's
                        // own units, so the same curve means the same thing
                        // whatever range the parameter happens to have.
                        useDaw.getState().writeParameter(
                          { kind: 'effect', trackId: track.id, effectId: effect.id, paramId: param.id },
                          normaliseParam(param, next),
                        );
                      }}
                      onPointerUp={() => {
                        useDaw.getState().releaseParameter({
                          kind: 'effect',
                          trackId: track.id,
                          effectId: effect.id,
                          paramId: param.id,
                        });
                        getServices().commands.breakCoalescing();
                      }}
                    />
                  )}
                  <span className="inspector__readout">{formatParamValue(param, value)}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A hosted plugin's row: where it came from, and its own interface on request.
 *
 * The interface is the plugin's, made by the plugin on the live node and
 * mounted here as it is; it is taken down through the plugin too. The
 * parameters the plugin reported are drawn below as ordinary controls, so a
 * plugin with no interface — or one that has not loaded — is still usable.
 */
function WamRow({ trackId, effect }: { trackId: string; effect: EffectInstance }): JSX.Element {
  return (
    <PluginRow
      name={effect.wamName ?? 'Plugin'}
      url={effect.wamUrl ?? ''}
      loaded={(effect.wamParameters ?? []).length > 0}
      getNode={() => getServices().engine.effectNode(trackId, effect.id)}
    />
  );
}

/** The same row for a plugin instrument: its state carries what an effect's instance does. */
function WamInstrumentRow({ trackId, instrument }: { trackId: string; instrument: InstrumentState }): JSX.Element {
  const data = instrument.data;
  return (
    <PluginRow
      name={typeof data.wamName === 'string' ? data.wamName : 'Plugin'}
      url={typeof data.wamUrl === 'string' ? data.wamUrl : ''}
      loaded={Array.isArray(data.wamParameters) && data.wamParameters.length > 0}
      getNode={() => getServices().engine.instrumentFor(trackId)}
    />
  );
}

interface PluginHost {
  createGui?(): Promise<Element>;
  destroyGui?(gui: Element): void;
}

function PluginRow({ name, url, loaded, getNode }: { name: string; url: string; loaded: boolean; getNode: () => PluginHost | null }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'shown' | 'failed'>('idle');
  const host = useRef<HTMLDivElement>(null);
  let origin = url;
  try {
    origin = new URL(url).host;
  } catch {
    /* shown as it is */
  }

  useEffect(() => {
    if (!open) return;
    const node = getNode();
    const mount = host.current;
    if (!node?.createGui || !mount) {
      setStatus('failed');
      return;
    }
    let gui: Element | null = null;
    let cancelled = false;
    setStatus('loading');
    node
      .createGui()
      .then((element) => {
        if (cancelled) {
          node.destroyGui?.(element);
          return;
        }
        gui = element;
        mount.replaceChildren(element);
        setStatus('shown');
      })
      .catch(() => {
        if (!cancelled) setStatus('failed');
      });
    return () => {
      cancelled = true;
      if (gui) {
        node.destroyGui?.(gui);
        gui.remove();
        // Whatever was changed through the interface is kept now, not only
        // at the next save.
        void useDaw.getState().captureWamStates();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the node is looked up when the interface opens
  }, [open, url]);

  return (
    <>
      <div className="inspector__row">
        <span className="inspector__label">Plugin</span>
        <span
          className="inspector__readout"
          style={{ gridColumn: '2 / 4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={`${url}\nThe plugin's code is fetched from here. Your audio stays on this computer.`}
        >
          {loaded ? '' : 'loading… '}
          {origin}
        </span>
      </div>
      <div className="inspector__row">
        <span className="inspector__label">Interface</span>
        <button
          className={`chip ${open ? 'chip--on' : ''}`}
          aria-pressed={open}
          aria-label={`${name} interface`}
          title="Show the plugin's own interface, as the plugin draws it"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'HIDE' : 'SHOW'}
        </button>
        {open && status === 'failed' && <span className="inspector__readout">no interface — the plugin has not loaded, or has none</span>}
      </div>
      {open && <div ref={host} className="inspector__wam" style={{ gridColumn: '1 / -1', overflow: 'auto', maxHeight: 480 }} />}
    </>
  );
}

/**
 * A reverb's room: the recording it convolves with, or the synthesised tail.
 *
 * Any audio asset the project holds can be the room, and a file can be loaded
 * straight into one. The tail's own controls disappear while a room is loaded,
 * because size and damping describe the tail, not the room.
 */
function ReverbRoomRow({ trackId, effect }: { trackId: string; effect: EffectInstance }): JSX.Element {
  const dispatch = useDaw((s) => s.dispatch);
  const assets = useDaw((s) => s.project.assets);
  const room = effect.impulseAssetId ? assets[effect.impulseAssetId] : undefined;
  const choices = Object.values(assets).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="inspector__row">
      <span className="inspector__label">Room</span>
      <div style={{ gridColumn: '2 / 4', display: 'flex', gap: 4, alignItems: 'center', minWidth: 0 }}>
        <select
          className="select"
          style={{ height: 20, flex: 1, minWidth: 0 }}
          value={effect.impulseAssetId ?? ''}
          aria-label="Reverb room"
          title="A recording of a real room to play through the reverb, or the synthesised tail"
          onChange={(e) =>
            dispatch(
              command(EffectCommands.setEffectImpulse, {
                trackId,
                effectId: effect.id,
                assetId: e.target.value === '' ? null : e.target.value,
              }),
            )
          }
        >
          <option value="">Synthesised tail</option>
          {choices.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.name}
            </option>
          ))}
        </select>
        <button
          className="chip"
          aria-label="Load a room recording"
          title="Import an impulse response — a recording of a real room — and play this reverb through it"
          onClick={() => {
            void (async () => {
              try {
                const [file] = await pickFiles('Impulse response', AUDIO_ACCEPT, false);
                if (file) await useDaw.getState().loadReverbRoom(trackId, effect.id, file);
              } catch {
                /* dialog dismissed */
              }
            })();
          }}
        >
          LOAD…
        </button>
        {room && <span className="inspector__readout">{room.durationSec.toFixed(2)}s</span>}
      </div>
    </div>
  );
}

/** `straight`, `late 0.40` and so on: a readout that says which way it bends. */
function formatCurvature(curvature: number): string {
  if (Math.abs(curvature) < 0.005) return 'straight';
  return `${curvature > 0 ? 'late' : 'early'} ${Math.abs(curvature).toFixed(2)}`;
}

const CURVE_HELP: Record<'linear' | 'hold' | 'smooth', string> = {
  linear: 'Straight line to the next point',
  hold: 'Stay at this value, then step at the next point',
  smooth: 'Ease out of this point and into the next',
};

/** Shows a lane's value at a position in the parameter's own units. */
function formatLaneValue(project: Project, lane: AutomationLane, tick: number): string {
  const descriptor = resolveParamDescriptor(project, lane.target);
  const normalised = automationValueAt(lane, tick);
  if (!descriptor || normalised === null) return '—';
  return formatParamValue(descriptor, denormaliseParam(descriptor, normalised));
}

/**
 * The marker the "Pin here" button would add: the moment of source audio under
 * the playhead, and the beat of the clip it currently lands on.
 *
 * Null when the playhead is not inside the clip, or is too close to either end
 * for a marker to survive: a marker there would be dropped when the curve is
 * built, and a button that silently does nothing is worse than one that is off.
 */
function warpMarkerAtPlayhead(
  project: Project,
  clip: AudioClip,
): { sourceSec: number; beat: number } | null {
  const totalBeats = warpTotalBeats(clip);
  if (totalBeats === null) return null;
  const beat = (project.transport.playheadTicks - clip.startTicks) / project.transport.ppq;
  if (beat <= 0 || beat >= totalBeats) return null;
  const sourceSec = beatToSourceSec(warpCurve(clip), beat);
  if (sourceSec < MIN_WARP_GAP_SEC || clip.durationSec - sourceSec < MIN_WARP_GAP_SEC) return null;
  if (beat < MIN_WARP_GAP_BEATS || totalBeats - beat < MIN_WARP_GAP_BEATS) return null;
  if (clip.warpMarkers.some((m) => Math.abs(m.sourceSec - sourceSec) < MIN_WARP_GAP_SEC)) return null;
  return { sourceSec, beat };
}

/**
 * A mark beside a parameter that is currently recording.
 *
 * Small on purpose: it has to be visible while a hand is on the control and
 * invisible the rest of the time, and a parameter list where most rows carry a
 * badge is a parameter list nobody reads.
 */
function WritingDot({ target }: { target: AutomationTarget }): JSX.Element | null {
  const writing = useDaw((s) => s.isWritingTarget(target));
  if (!writing) return null;
  return <span className="writing-dot" aria-label={`Recording ${target.paramId}`} />;
}

/**
 * One ride point: where it is, and a level that can be typed.
 *
 * The field shows the level in decibels and commits on change, so typing "-6"
 * moves the point to exactly -6.0 dB rather than to wherever a drag happened to
 * stop. The point keeps its place in the source; only the level moves.
 */
function RidePointRow({ clipId, point }: { clipId: string; point: ClipGainPoint }): JSX.Element {
  const dispatch = useDaw((s) => s.dispatch);
  const db = point.gain > 0 ? 20 * Math.log10(point.gain) : RIDE_FLOOR_DB;
  const shown = Math.max(RIDE_FLOOR_DB, Math.round(db * 10) / 10);
  return (
    <>
      <span className="inspector__readout" title="Position in the source, in seconds">
        {point.sourceSec.toFixed(3)}s
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        <input
          className="numeric"
          style={{ width: 56 }}
          type="number"
          min={RIDE_FLOOR_DB}
          max={6}
          step={0.1}
          value={shown}
          aria-label={`Ride level at ${point.sourceSec.toFixed(3)}s in dB`}
          title={`${formatRideDb(point.gain)}. Type a level in decibels; −60 and below is silence.`}
          onChange={(e) =>
            dispatch(
              command(ClipGainCommands.moveClipGainPoint, {
                clipId,
                pointId: point.id,
                sourceSec: point.sourceSec,
                gain: rideGainFromDb(Number(e.target.value)),
              }),
            )
          }
          onBlur={() => getServices().commands.breakCoalescing()}
        />
        <span className="inspector__readout">{formatRideDb(point.gain)}</span>
      </span>
    </>
  );
}

/** A speed as a musician reads it: a multiple, and how far off the original it is. */
function formatRate(rate: number): string {
  const percent = Math.round((1 / rate) * 1000) / 10;
  return `${rate.toFixed(3)}x · ${percent}%`;
}

/** The whole-semitone part of a shift, rounded towards zero so the cents keep its sign. */
function wholeSemitones(semitones: number): number {
  return semitones < 0 ? Math.ceil(semitones) : Math.floor(semitones);
}

/** The cents left over once the whole semitones are taken out. */
function centsPart(semitones: number): number {
  return Math.round((quantiseSemitones(semitones) - wholeSemitones(quantiseSemitones(semitones))) * 100);
}

/**
 * A transposition as a musician reads it. Semitones and cents rather than a
 * frequency ratio: nobody asks for 1.498x, they ask for a fifth.
 */
function formatSemitones(semitones: number): string {
  const value = quantiseSemitones(semitones);
  if (value === 0) return 'unison';
  const whole = wholeSemitones(value);
  const cents = centsPart(value);
  const signed = (n: number) => `${n > 0 ? '+' : ''}${n}`;
  if (whole === 0) return `${signed(cents)} c`;
  return cents === 0 ? `${signed(whole)} st` : `${signed(whole)} st ${signed(cents)} c`;
}

/** Which take a comp clip is, when the track has takes and the clip is from one. */
function CompSourceRow({ clip }: { clip: AudioClip }): JSX.Element | null {
  const source = useDaw((s) => {
    const track = s.project.tracks[clip.trackId];
    if (!track || clip.takeLane !== 0 || takeLaneCount(s.project, clip.trackId) === 0) return null;
    const lane = compSources(s.project, clip.trackId).find((c) => c.clipId === clip.id)?.takeLane ?? null;
    return lane === null ? 'none of the takes' : trackTakeName(track, lane);
  });
  if (source === null) return null;
  return (
    <div className="inspector__row">
      <span className="inspector__label">From</span>
      <span className="inspector__readout" style={{ gridColumn: '2 / 4' }} aria-label="Take this clip came from">
        {source}
      </span>
    </div>
  );
}

function ClipSection({
  clip,
  lengthLabel,
  rate,
}: {
  clip: AudioClip;
  lengthLabel: string;
  rate: number;
}): JSX.Element {
  const dispatch = useDaw((s) => s.dispatch);
  const hasRideOnClipboard = useDaw((s) => s.clipGainClipboard !== null);
  const warping = clip.warpSourceBpm !== null;
  const markerAtPlayhead = useDaw((s) => warpMarkerAtPlayhead(s.project, clip));
  const transposed = Math.abs(quantiseSemitones(clip.pitchSemitones)) > 0;

  return (
    <div className="inspector__group">
      <div className="inspector__title">Clip</div>

      <div className="inspector__row">
        <span className="inspector__label">Name</span>
        <input
          className="numeric"
          style={{ gridColumn: '2 / 4', background: 'var(--bg-2)', border: '1px solid var(--line)', borderRadius: 3, padding: '2px 5px' }}
          value={clip.name}
          aria-label="Clip name"
          onChange={(e) => dispatch(command(ClipCommands.renameClip, { clipId: clip.id, name: e.target.value || 'Clip' }))}
        />
      </div>

      <CompSourceRow clip={clip} />

      <div className="inspector__row">
        <span className="inspector__label">Gain</span>
        <input
          className="slider"
          type="range"
          min={-40}
          max={12}
          step={0.1}
          value={Number.isFinite(gainToDb(clip.gain)) ? gainToDb(clip.gain) : -40}
          aria-label="Clip gain"
          onChange={(e) => dispatch(command(ClipCommands.setClipGain, { clipIds: [clip.id], gain: dbToGain(Number(e.target.value)) }))}
          onPointerUp={() => getServices().commands.breakCoalescing()}
        />
        <span className="inspector__readout">{gainToDb(clip.gain).toFixed(1)} dB</span>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Reverse</span>
        <div style={{ gridColumn: '2 / 4', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <button
            className={`chip ${clip.reversed ? 'chip--mon chip--on' : ''}`}
            aria-pressed={clip.reversed}
            aria-label="Play the clip backwards"
            title="Play this clip backwards. The file is untouched, warp markers turn round with the audio, and unreversing puts it all back."

            onClick={() =>
              dispatch(command(ClipCommands.setClipReversed, { clipIds: [clip.id], reversed: !clip.reversed }))
            }
          >
            {clip.reversed ? 'BACKWARDS' : 'FORWARDS'}
          </button>
        </div>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Ride</span>
        <div style={{ gridColumn: '2 / 4', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <button
            className="chip"
            disabled={clip.gainEnvelope.length === 0}
            onClick={() => useDaw.getState().copyClipGainRide()}
            title="Copy this clip's gain ride, to put on another clip"
            aria-label="Copy ride"
          >
            COPY
          </button>
          <button
            className="chip"
            disabled={!hasRideOnClipboard}
            onClick={() => useDaw.getState().pasteClipGainRide()}
            title="Put the copied ride on the selected clips, at the same place inside each one"
            aria-label="Paste ride"
          >
            PASTE
          </button>
          <button
            className="chip"
            disabled={!hasRideOnClipboard}
            onClick={() => useDaw.getState().pasteClipGainRide(true)}
            title="Put the copied ride on the selected clips with its first point at the playhead"
            aria-label="Paste ride at the playhead"
          >
            PASTE @
          </button>
          <button
            className="chip"
            disabled={clip.gainEnvelope.length === 0}
            onClick={() =>
              dispatch(command(ClipGainCommands.flattenClipGain, { clipId: clip.id, atSourceSec: clip.offsetSec }))
            }
            title="Fold the ride into the clip's gain, taking the level it holds at the clip's start"
            aria-label="Flatten ride"
          >
            FLATTEN
          </button>
          <button
            className="chip"
            disabled={clip.gainEnvelope.length === 0}
            onClick={() => dispatch(command(ClipGainCommands.clearClipGain, { clipIds: [clip.id] }))}
            title="Remove the ride entirely"
            aria-label="Clear ride"
          >
            ✕
          </button>
          <span className="inspector__readout" style={{ marginLeft: 'auto' }}>
            {clip.gainEnvelope.length === 0 ? 'none' : `${clip.gainEnvelope.length} pts`}
          </span>
        </div>
      </div>

      {clip.gainEnvelope.length > 2 && (
        <div className="inspector__row">
          <span className="inspector__label">Thin</span>
          <div style={{ display: 'flex', gap: 4, gridColumn: '2 / 4' }}>
            {/*
              The same three settings an automation lane offers, meaning the
              same thing: how far the level may be wrong, as a fraction of the
              full range, for a point to be dropped.
            */}
            {([
              ['Light', 0.005],
              ['Medium', 0.02],
              ['Heavy', 0.06],
            ] as const).map(([label, tolerance]) => (
              <button
                key={label}
                className="btn"
                style={{ height: 20, flex: 1 }}
                aria-label={`Thin the ride, ${label.toLowerCase()}`}
                title="Remove points the line through their neighbours already covers. Points that start or end a held or smooth segment stay."
                onClick={() => dispatch(command(ClipGainCommands.thinClipGain, { clipIds: [clip.id], tolerance }))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {clip.gainEnvelope.length > 0 && (
        <div className="inspector__row" style={{ alignItems: 'flex-start' }}>
          <span className="inspector__label">Points</span>
          {/*
            Every point, with its level as a number that can be typed. The
            canvas draws the same figures beside the handles; this is where one
            is set exactly, because a slider or a drag is the wrong instrument
            for "make that -6.0". Times are source seconds, which is what the
            ride is measured in and why it survives a trim.
          */}
          <div
            style={{ gridColumn: '2 / 4', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', maxHeight: 132, overflowY: 'auto' }}
            aria-label="Ride points"
          >
            {sortedGainPoints(clip.gainEnvelope).map((point) => (
              <RidePointRow key={point.id} clipId={clip.id} point={point} />
            ))}
          </div>
        </div>
      )}

      <div className="inspector__row">
        <span className="inspector__label">Speed</span>
        <input
          className="slider"
          type="range"
          min={Math.log2(MIN_PLAYBACK_RATE)}
          max={Math.log2(MAX_PLAYBACK_RATE)}
          step={0.005}
          // Logarithmic: half speed and double speed should sit the same
          // distance from the centre, because they are the same musical move.
          value={Math.log2(rate)}
          aria-label="Clip speed"
          title="How fast the clip plays. Its length on the timeline changes to match."
          onChange={(e) =>
            dispatch(
              command(ClipCommands.setClipRate, {
                clipIds: [clip.id],
                rate: Math.pow(2, Number(e.target.value)),
                preservePitch: null,
              }),
            )
          }
          onPointerUp={() => getServices().commands.breakCoalescing()}
          onDoubleClick={() =>
            dispatch(command(ClipCommands.setClipRate, { clipIds: [clip.id], rate: 1, preservePitch: null }))
          }
        />
        <span className="inspector__readout">{formatRate(rate)}</span>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Pitch</span>
        <div style={{ gridColumn: '2 / 3', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <input
            className="slider"
            style={{ flex: 1, minWidth: 0 }}
            type="range"
            min={MIN_PITCH_SEMITONES}
            max={MAX_PITCH_SEMITONES}
            // Whole semitones on the slider; the cents field beside it is what
            // tunes a sample to a track, and a slider is the wrong instrument
            // for one part in twenty-four hundred.
            step={1}
            value={wholeSemitones(clip.pitchSemitones)}
            aria-label="Clip pitch in semitones"
            title="Transpose the clip. Its length on the timeline does not change."
            onChange={(e) =>
              dispatch(
                command(ClipCommands.setClipPitch, {
                  clipIds: [clip.id],
                  semitones: Number(e.target.value) + centsPart(clip.pitchSemitones) / 100,
                }),
              )
            }
            onPointerUp={() => getServices().commands.breakCoalescing()}
            onDoubleClick={() =>
              dispatch(command(ClipCommands.setClipPitch, { clipIds: [clip.id], semitones: 0 }))
            }
          />
          <input
            className="numeric"
            style={{ width: 46 }}
            type="number"
            min={-99}
            max={99}
            step={1}
            value={centsPart(clip.pitchSemitones)}
            aria-label="Clip pitch in cents"
            title="Fine tuning, in cents. A hundred cents is one semitone."
            onChange={(e) => {
              const cents = Math.max(-99, Math.min(99, Math.round(Number(e.target.value) || 0)));
              dispatch(
                command(ClipCommands.setClipPitch, {
                  clipIds: [clip.id],
                  semitones: wholeSemitones(clip.pitchSemitones) + cents / 100,
                }),
              );
            }}
            onBlur={() => getServices().commands.breakCoalescing()}
          />
        </div>
        <span className="inspector__readout">{formatSemitones(clip.pitchSemitones)}</span>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Formants</span>
        <div style={{ gridColumn: '2 / 3', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <button
            className={`chip ${clip.formantSemitones !== null ? 'chip--mon chip--on' : ''}`}
            aria-pressed={clip.formantSemitones !== null}
            aria-label="Set the formants independently of the pitch"
            title={
              'The resonances of the body making the sound. Left to follow, they move with the pitch and a ' +
              'transposed voice changes size. Set, they sit where you put them — at 0 the singer is unchanged, ' +
              'and away from 0 they are a different size at the same note.'
            }
            onClick={() =>
              dispatch(
                command(ClipCommands.setClipFormants, {
                  clipIds: [clip.id],
                  semitones: clip.formantSemitones === null ? 0 : null,
                }),
              )
            }
          >
            {clip.formantSemitones === null ? 'FOLLOW' : 'SET'}
          </button>
          <input
            className="slider"
            style={{ flex: 1, minWidth: 0 }}
            type="range"
            min={MIN_PITCH_SEMITONES}
            max={MAX_PITCH_SEMITONES}
            step={0.5}
            value={clip.formantSemitones ?? 0}
            disabled={clip.formantSemitones === null}
            aria-label="Formant position in semitones"
            title="Where the formants sit, in semitones from where they were recorded."
            onChange={(e) =>
              dispatch(
                command(ClipCommands.setClipFormants, {
                  clipIds: [clip.id],
                  semitones: Number(e.target.value),
                }),
              )
            }
            onPointerUp={() => getServices().commands.breakCoalescing()}
            onDoubleClick={() =>
              dispatch(command(ClipCommands.setClipFormants, { clipIds: [clip.id], semitones: 0 }))
            }
          />
        </div>
        <span className="inspector__readout">
          {clip.formantSemitones === null
            ? transposed
              ? 'with pitch'
              : 'unchanged'
            : formatSemitones(clip.formantSemitones)}
        </span>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Tempo</span>
        <div style={{ gridColumn: '2 / 4', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <button
          className={`btn btn--ghost ${warping ? 'btn--active' : ''}`}
          aria-pressed={warping}
          aria-label="Follow the project tempo"
          title={
            warping
              ? 'This clip refits itself whenever the project tempo changes. Click to fix its speed instead.'
              : 'Fix the clip at its current speed. Click to make it follow the project tempo.'
          }
          onClick={() =>
            dispatch(
              command(ClipCommands.setClipWarp, {
                clipIds: [clip.id],
                follow: !warping,
                sourceBpm: null,
              }),
            )
          }
        >
          {warping ? 'Following' : 'Fixed'}
        </button>
        {warping && (
          <input
            className="numeric"
            style={{ width: 46 }}
            type="number"
            min={5}
            max={900}
            step={0.5}
            value={Math.round((clip.warpSourceBpm ?? 120) * 100) / 100}
            aria-label="Clip source tempo"
            title="The tempo this audio is fitted to the grid at: what it was recorded at, or what keeps its beats in place after a marked clip was trimmed. The clip is refitted from it."
            onChange={(e) =>
              dispatch(
                command(ClipCommands.setClipWarp, {
                  clipIds: [clip.id],
                  follow: true,
                  sourceBpm: Number(e.target.value),
                }),
              )
            }
            onBlur={() => getServices().commands.breakCoalescing()}
          />
        )}
        {warping && (
          <button
            className="btn btn--ghost"
            aria-label="Detect the clip source tempo"
            title="Measure the tempo of this clip's own audio and use it. Works on anything with a steady pulse."
            onClick={() => void useDaw.getState().detectClipTempo(clip.id)}
          >
            Detect
          </button>
        )}
        {warping && (
          <button
            className="btn btn--ghost"
            aria-label="Warp the clip to its beats"
            title="Find where every beat of this clip's audio landed and pin each one to the grid, so a performance that pushes and drags plays straight. Replaces the clip's markers."
            onClick={() => void useDaw.getState().warpClipToBeats(clip.id)}
          >
            Beats
          </button>
        )}
        <button
          className="btn btn--ghost"
          aria-label="Make the tempo map follow this clip"
          title="Find where every beat of this clip's audio landed and bend the project's tempo map to them, so the clip plays exactly as recorded and the grid follows it. The clip stops following the tempo."
          onClick={() => void useDaw.getState().tempoMapFromClip(clip.id)}
        >
          Grid ← clip
        </button>
        </div>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Pitch</span>
        <button
          className={`btn btn--ghost ${clip.preservePitch ? 'btn--active' : ''}`}
          style={{ gridColumn: '2 / 4', justifySelf: 'start' }}
          aria-pressed={clip.preservePitch}
          aria-label="Preserve pitch when stretching"
          title={
            clip.preservePitch
              ? 'Stretching keeps the pitch. Click for varispeed, where pitch follows speed.'
              : 'Varispeed: the pitch follows the speed, like a tape machine. Click to keep the pitch.'
          }
          onClick={() =>
            dispatch(
              command(ClipCommands.setClipRate, {
                clipIds: [clip.id],
                rate: clip.playbackRate,
                preservePitch: !clip.preservePitch,
              }),
            )
          }
        >
          {clip.preservePitch ? 'Keep pitch' : 'Varispeed'}
        </button>
      </div>

      {warping && (
        <div className="inspector__row">
          <span className="inspector__label">Warp</span>
          <button
            className="btn btn--ghost"
            aria-label="Add a warp marker at the playhead"
            title="Pin whatever is playing at the playhead to the beat it lands on, so the audio either side of it is fitted separately."
            disabled={markerAtPlayhead === null}
            onClick={() => {
              if (!markerAtPlayhead) return;
              dispatch({
                type: ClipCommands.addWarpMarker.type,
                params: { clipId: clip.id, ...markerAtPlayhead },
              });
            }}
          >
            Pin here
          </button>
          <span className="inspector__readout">
            {clip.warpMarkers.length === 0
              ? 'none'
              : `${clip.warpMarkers.length} marker${clip.warpMarkers.length === 1 ? '' : 's'}`}
          </span>
        </div>
      )}

      {warping &&
        clip.warpMarkers.map((marker) => (
          <div className="inspector__row" key={marker.id}>
            <span className="inspector__label">{marker.sourceSec.toFixed(2)}s</span>
            <input
              className="numeric"
              style={{ width: 56 }}
              type="number"
              min={0}
              max={10000}
              step={0.25}
              value={Math.round(marker.beat * 1000) / 1000}
              aria-label={`Warp marker at ${marker.sourceSec.toFixed(2)} seconds, in beats`}
              title="Which beat of the clip this moment of audio lands on."
              onChange={(e) =>
                dispatch(
                  command(ClipCommands.moveWarpMarker, {
                    clipId: clip.id,
                    markerId: marker.id,
                    beat: Number(e.target.value),
                  }),
                )
              }
              onBlur={() => getServices().commands.breakCoalescing()}
            />
            <button
              className="btn btn--ghost btn--icon"
              aria-label={`Remove the warp marker at ${marker.sourceSec.toFixed(2)} seconds`}
              onClick={() =>
                dispatch(command(ClipCommands.removeWarpMarker, { clipId: clip.id, markerId: marker.id }))
              }
            >
              {'\u2212'}
            </button>
          </div>
        ))}

      <div className="inspector__row">
        <span className="inspector__label">Fade in</span>
        <input
          className="slider"
          type="range"
          min={0}
          max={Math.max(0.01, clip.durationSec)}
          step={0.001}
          value={clip.fadeIn.durationSec}
          aria-label="Fade in length"
          onChange={(e) =>
            dispatch(command(ClipCommands.setClipFade, { clipId: clip.id, edge: 'in', durationSec: Number(e.target.value), curve: null, curvature: null }))
          }
          onPointerUp={() => getServices().commands.breakCoalescing()}
        />
        <span className="inspector__readout">{(clip.fadeIn.durationSec * 1000).toFixed(0)} ms</span>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">In shape</span>
        <input
          className="slider"
          type="range"
          min={-1}
          max={1}
          step={0.01}
          value={clip.fadeIn.curvature}
          disabled={clip.fadeIn.durationSec <= 0}
          aria-label="Fade in shape"
          title="Bend the fade: left rises early, right rises late"
          onChange={(e) =>
            dispatch({
              type: ClipCommands.setClipFade.type,
              params: { clipId: clip.id, edge: 'in', curvature: Number(e.target.value) },
            })
          }
          onPointerUp={() => getServices().commands.breakCoalescing()}
        />
        <span className="inspector__readout">{formatCurvature(clip.fadeIn.curvature)}</span>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Fade out</span>
        <input
          className="slider"
          type="range"
          min={0}
          max={Math.max(0.01, clip.durationSec)}
          step={0.001}
          value={clip.fadeOut.durationSec}
          aria-label="Fade out length"
          onChange={(e) =>
            dispatch(command(ClipCommands.setClipFade, { clipId: clip.id, edge: 'out', durationSec: Number(e.target.value), curve: null, curvature: null }))
          }
          onPointerUp={() => getServices().commands.breakCoalescing()}
        />
        <span className="inspector__readout">{(clip.fadeOut.durationSec * 1000).toFixed(0)} ms</span>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Out shape</span>
        <input
          className="slider"
          type="range"
          min={-1}
          max={1}
          step={0.01}
          value={clip.fadeOut.curvature}
          disabled={clip.fadeOut.durationSec <= 0}
          aria-label="Fade out shape"
          title="Bend the fade: left falls late, right falls early"
          onChange={(e) =>
            dispatch({
              type: ClipCommands.setClipFade.type,
              params: { clipId: clip.id, edge: 'out', curvature: Number(e.target.value) },
            })
          }
          onPointerUp={() => getServices().commands.breakCoalescing()}
        />
        <span className="inspector__readout">{formatCurvature(clip.fadeOut.curvature)}</span>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Fade law</span>
        <select
          className="select"
          style={{ height: 20, gridColumn: '2 / 4' }}
          value={clip.fadeIn.curve}
          aria-label="Fade law"
          onChange={(e) => {
            for (const edge of ['in', 'out'] as const) {
              dispatch({
                type: ClipCommands.setClipFade.type,
                params: { clipId: clip.id, edge, curve: e.target.value },
              });
            }
          }}
        >
          <option value="equalPower">Equal power</option>
          <option value="linear">Linear</option>
          <option value="exponential">Exponential</option>
        </select>
      </div>

      <div className="inspector__row">
        <span className="inspector__label">Source</span>
        <span className="inspector__readout" style={{ gridColumn: '2 / 4' }}>
          {clip.offsetSec.toFixed(3)}s → {(clip.offsetSec + clip.durationSec).toFixed(3)}s
        </span>
      </div>
      <div className="inspector__row">
        <span className="inspector__label">Length</span>
        <span className="inspector__readout" style={{ gridColumn: '2 / 4' }}>
          {clip.durationSec.toFixed(3)}s · {lengthLabel}
        </span>
      </div>
    </div>
  );
}
